import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useController, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { FormDatePicker } from '@/components/form/FormDatePicker'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { FormTagsInput } from '@/components/form/FormTagsInput'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { useToast } from '@/components/ui/toastContext'
import { COMMON_SKILLS } from '@/constants/skills'
import { useCandidateFilterOptions } from '@/features/recruiter/hooks/useCandidates'
import { JobLocationFields } from '@/features/recruiter/components/JobLocationFields'
import { JobScreeningQuestionsSection } from '@/features/recruiter/components/JobScreeningQuestionsSection'
import { JobStatusActions } from '@/features/recruiter/components/JobStatusActions'
import { jobsApi } from '@/features/recruiter/api/jobs.api'
import type { JobPayload } from '@/features/recruiter/api/jobs.api'
import {
  useCreateJob,
  useDeleteJd,
  useRecruiterJobQuery,
  useUpdateJob,
  useUploadJd,
} from '@/features/recruiter/hooks/useJobs'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const OPENINGS_OPTIONS = [
  ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  { value: '15', label: '15' },
  { value: '20', label: '20' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
]

const EMPLOYMENT_TYPE_OPTIONS = [
  'full_time',
  'part_time',
  'internship',
  'contract',
  'freelance',
].map((value) => ({ value, label: humanizeEnum(value) }))

const WORK_MODE_OPTIONS = ['onsite', 'remote', 'hybrid'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

// Experience is picked in whole years; the API stores months (years × 12).
const yearLabel = (n: number) => `${n} ${n === 1 ? 'year' : 'years'}`

const EXPERIENCE_MIN_OPTIONS = [
  { value: '0', label: 'Fresher (0 years)' },
  ...Array.from({ length: 20 }, (_, i) => ({ value: String(i + 1), label: yearLabel(i + 1) })),
]

const EXPERIENCE_MAX_OPTIONS = Array.from({ length: 30 }, (_, i) => ({
  value: String(i + 1),
  label: yearLabel(i + 1),
}))

const monthsToYears = (months: number | null) =>
  months != null ? String(Math.round(months / 12)) : ''

const yearsToMonths = (years: string | undefined) => (years ? Number(years) * 12 : undefined)

// Salary is picked in LPA (lakh per annum, INR); the API stores annual rupees.
const RUPEES_PER_LAKH = 100000

const SALARY_LPA_STEPS = [
  1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 6, 7, 8, 9, 10, 12, 14, 15, 16, 18, 20, 22, 25, 28, 30, 35, 40,
  45, 50, 60, 70, 80, 90, 100,
]

const lpaLabel = (lpa: number) => `₹${lpa} LPA`

const SALARY_LPA_OPTIONS = SALARY_LPA_STEPS.map((n) => ({ value: String(n), label: lpaLabel(n) }))

// API decimals arrive as strings ("350000.00"), hence Number().
const rupeesToLpa = (rupees: number | string | null | undefined) =>
  rupees != null ? String(Number(rupees) / RUPEES_PER_LAKH) : ''

const lpaToRupees = (lpa: string | undefined) =>
  lpa ? Math.round(Number(lpa) * RUPEES_PER_LAKH) : undefined

// Keeps an already-saved salary that isn't one of the standard steps selectable.
function withSavedLpa(options: { value: string; label: string }[], saved: string) {
  if (saved === '' || options.some((o) => o.value === saved)) return options
  return [...options, { value: saved, label: lpaLabel(Number(saved)) }].sort(
    (a, b) => Number(a.value) - Number(b.value),
  )
}

const NOTICE_PERIOD_STEPS = [0, 15, 30, 45, 60, 90]

const noticeLabel = (days: number) => (days === 0 ? 'Immediate joiner' : `${days} days`)

const NOTICE_PERIOD_OPTIONS = NOTICE_PERIOD_STEPS.map((d) => ({
  value: String(d),
  label: noticeLabel(d),
}))

// Local calendar date as YYYY-MM-DD — the same format the date picker emits,
// so plain string comparison works for "not in the past".
function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const schema = z
  .object({
    title: z.string().min(1, 'Title is required').max(255),
    number_of_openings: z.string().optional(),
    description: z.string().min(1, 'Description is required'),
    employment_type: z.enum(['full_time', 'part_time', 'internship', 'contract', 'freelance']),
    work_mode: z.enum(['onsite', 'remote', 'hybrid']),
    location_state: z.string().min(1, 'Select a state or union territory.'),
    location_city: z.string().max(100).optional(),
    experience_min_years: z.string().optional(),
    experience_max_years: z.string().optional(),
    salary_min_lpa: z.string().optional(),
    salary_max_lpa: z.string().optional(),
    skills_required_json: z.array(z.string()).optional(),
    education_required_json: z.array(z.string()).optional(),
    notice_period_max_days: z.string().optional(),
    application_deadline: z.string().optional(),
  })
  .refine(
    (v) =>
      !v.experience_min_years ||
      !v.experience_max_years ||
      Number(v.experience_max_years) >= Number(v.experience_min_years),
    { message: 'Max experience must be greater than or equal to min experience.', path: ['experience_max_years'] },
  )
  .refine(
    (v) => !v.salary_min_lpa || !v.salary_max_lpa || Number(v.salary_max_lpa) >= Number(v.salary_min_lpa),
    { message: 'Max salary must be greater than or equal to min salary.', path: ['salary_max_lpa'] },
  )
  .refine((v) => !v.application_deadline || v.application_deadline >= todayIso(), {
    message: "Application deadline can't be in the past.",
    path: ['application_deadline'],
  })

type FormValues = z.infer<typeof schema>

const EMPTY_VALUES: FormValues = {
  title: '',
  number_of_openings: '1',
  description: '',
  employment_type: 'full_time',
  work_mode: 'onsite',
  location_state: '',
  location_city: '',
  experience_min_years: '',
  experience_max_years: '',
  salary_min_lpa: '',
  salary_max_lpa: '',
  skills_required_json: [],
  education_required_json: [],
  notice_period_max_days: '',
  application_deadline: '',
}

export function JobFormPage() {
  const params = useParams<{ jobId: string }>()
  const jobId = params.jobId ? Number(params.jobId) : undefined
  const isEditing = jobId != null
  const navigate = useNavigate()
  const toast = useToast()

  const { data: job, isPending: isLoadingJob } = useRecruiterJobQuery(jobId)
  const create = useCreateJob()
  const update = useUpdateJob(jobId ?? 0)
  const uploadJd = useUploadJd(jobId ?? 0)
  const deleteJd = useDeleteJd(jobId ?? 0)
  const [pendingJdFile, setPendingJdFile] = useState<File | null>(null)
  const jdInputRef = useRef<HTMLInputElement>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_VALUES })

  const {
    field: { value: stateValue, onChange: onStateChange },
    fieldState: { error: stateError },
  } = useController({ control, name: 'location_state' })
  const {
    field: { value: cityValue, onChange: onCityChange },
    fieldState: { error: cityError },
  } = useController({ control, name: 'location_city' })

  useEffect(() => {
    if (!job) return
    reset({
      title: job.title,
      number_of_openings: String(job.numberOfOpenings ?? 1),
      description: job.description,
      employment_type: job.employmentType,
      work_mode: job.workMode,
      location_state: job.locationState ?? '',
      location_city: job.locationCity ?? '',
      experience_min_years: monthsToYears(job.experienceMinMonths),
      experience_max_years: monthsToYears(job.experienceMaxMonths),
      salary_min_lpa: rupeesToLpa(job.salaryMin),
      salary_max_lpa: rupeesToLpa(job.salaryMax),
      skills_required_json: job.skillsRequired ?? [],
      education_required_json: job.educationRequired ?? [],
      notice_period_max_days: job.noticePeriodMaxDays != null ? String(job.noticePeriodMaxDays) : '',
      application_deadline: job.applicationDeadline ?? '',
    })
  }, [job, reset])

  const salaryOptions = withSavedLpa(
    withSavedLpa(SALARY_LPA_OPTIONS, rupeesToLpa(job?.salaryMin)),
    rupeesToLpa(job?.salaryMax),
  )

  // Curated common skills plus real student skills, de-duplicated ignoring case.
  const { data: filterOptions } = useCandidateFilterOptions()
  const skillSuggestions = [...COMMON_SKILLS, ...(filterOptions?.skills ?? [])].filter(
    (skill, i, all) => all.findIndex((s) => s.toLowerCase() === skill.toLowerCase()) === i,
  )

  const savedNotice = job?.noticePeriodMaxDays != null ? String(job.noticePeriodMaxDays) : ''
  const noticeOptions =
    savedNotice === '' || NOTICE_PERIOD_OPTIONS.some((o) => o.value === savedNotice)
      ? NOTICE_PERIOD_OPTIONS
      : [...NOTICE_PERIOD_OPTIONS, { value: savedNotice, label: noticeLabel(Number(savedNotice)) }].sort(
          (a, b) => Number(a.value) - Number(b.value),
        )

  const mutation = isEditing ? update : create

  const onSubmit = handleSubmit((values) => {
    const payload: JobPayload = {
      title: values.title,
      number_of_openings: values.number_of_openings ? Number(values.number_of_openings) : undefined,
      description: values.description,
      employment_type: values.employment_type,
      work_mode: values.work_mode,
      location_state: values.location_state,
      location_city: values.location_city?.trim() || undefined,
      location_country: 'India',
      experience_min_months: yearsToMonths(values.experience_min_years),
      experience_max_months: yearsToMonths(values.experience_max_years),
      salary_min: lpaToRupees(values.salary_min_lpa),
      salary_max: lpaToRupees(values.salary_max_lpa),
      currency: 'INR',
      skills_required_json: values.skills_required_json,
      education_required_json: values.education_required_json,
      notice_period_max_days: values.notice_period_max_days
        ? Number(values.notice_period_max_days)
        : undefined,
      application_deadline: values.application_deadline || undefined,
    }

    if (isEditing) {
      update.mutate(payload, {
        onSuccess: () => toast.success('Job updated.'),
      })
    } else {
      create.mutate(payload, {
        onSuccess: (created) => {
          if (pendingJdFile) {
            void jobsApi.uploadJd(created.id, pendingJdFile)
          }
          toast.success('Job created as a draft.')
          void navigate(`/recruiter/jobs/${created.id}/edit`, { replace: true })
        },
      })
    }
  })

  if (isEditing && isLoadingJob) {
    return <p className="text-muted my-4">Loading job…</p>
  }

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <div className="d-flex align-items-center gap-2">
            <p className="fw-semibold fs-20 mb-0">{isEditing ? 'Edit Job' : 'Post a Job'}</p>
            {job && <StatusPill status={job.status} />}
          </div>
        </div>
        {job && <JobStatusActions jobId={job.id} status={job.status} />}
      </div>

      <div className="row g-3">
        <div className="col-xl-8">
          <Card title="Job Details">
            <FormErrorSummary
              errors={errors}
              serverErrors={getFieldErrors(mutation.error)}
              generalMessage={mutation.error ? getErrorMessage(mutation.error) : null}
            />
            <form onSubmit={(e) => void onSubmit(e)} noValidate>
              <div className="row">
                <div className="col-md-9">
                  <FormInput control={control} name="title" label="Job Title" required />
                </div>
                <div className="col-md-3">
                  <FormSelect
                    control={control}
                    name="number_of_openings"
                    label="No. of Openings"
                    options={OPENINGS_OPTIONS}
                  />
                </div>
              </div>
              <FormTextarea
                control={control}
                name="description"
                label="Description"
                rows={8}
                required
              />
              <div className="row">
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="employment_type"
                    label="Employment Type"
                    options={EMPLOYMENT_TYPE_OPTIONS}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="work_mode"
                    label="Work Mode"
                    options={WORK_MODE_OPTIONS}
                    required
                  />
                </div>
              </div>
              <JobLocationFields
                state={stateValue ?? ''}
                city={cityValue ?? ''}
                onStateChange={onStateChange}
                onCityChange={onCityChange}
                stateError={stateError?.message}
                cityError={cityError?.message}
              />
              <div className="row">
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="experience_min_years"
                    label="Min Experience"
                    options={EXPERIENCE_MIN_OPTIONS}
                    placeholder="Any"
                    clearable
                  />
                </div>
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="experience_max_years"
                    label="Max Experience"
                    options={EXPERIENCE_MAX_OPTIONS}
                    placeholder="Any"
                    clearable
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="salary_min_lpa"
                    label="Min Salary (LPA)"
                    options={salaryOptions}
                    placeholder="Not disclosed"
                    clearable
                  />
                </div>
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="salary_max_lpa"
                    label="Max Salary (LPA)"
                    options={salaryOptions}
                    placeholder="Not disclosed"
                    clearable
                  />
                </div>
              </div>
              <FormTagsInput
                control={control}
                name="skills_required_json"
                label="Required Skills"
                placeholder="Start typing a skill, e.g. HTML"
                suggestions={skillSuggestions}
              />
              <FormTagsInput
                control={control}
                name="education_required_json"
                label="Required Education"
                placeholder="e.g. B.Tech — press Enter to add"
              />
              <div className="row">
                <div className="col-md-6">
                  <FormSelect
                    control={control}
                    name="notice_period_max_days"
                    label="Max Notice Period"
                    options={noticeOptions}
                    placeholder="Any"
                    clearable
                  />
                </div>
                <div className="col-md-6">
                  <FormDatePicker
                    control={control}
                    name="application_deadline"
                    label="Application Deadline"
                    minDate="today"
                  />
                </div>
              </div>
              {/* JD File Upload */}
              <div className="mb-3">
                <label className="form-label fs-14">
                  Job Description File
                  <span className="text-muted fs-12 fw-normal ms-2">(PDF or Word, optional, max 10 MB)</span>
                </label>

                {/* Show existing file when editing */}
                {isEditing && job?.jdFileUrl && (
                  <div className="d-flex align-items-center gap-2 mb-2 p-2 rounded border bg-light">
                    <i className="ti ti-file-text fs-18 text-primary" />
                    <a
                      href={job.jdFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fs-13 text-primary fw-medium text-decoration-none flex-fill"
                    >
                      View current JD
                    </a>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger py-0 px-2 fs-12"
                      disabled={deleteJd.isPending}
                      onClick={() => deleteJd.mutate(undefined, { onSuccess: () => toast.success('JD removed.') })}
                    >
                      {deleteJd.isPending ? <span className="spinner-border spinner-border-sm" /> : <i className="ti ti-trash fs-13" />}
                    </button>
                  </div>
                )}

                <div className="d-flex align-items-center gap-2">
                  <input
                    ref={jdInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="form-control fs-13"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      if (!file) return
                      if (isEditing && jobId) {
                        uploadJd.mutate(file, {
                          onSuccess: () => {
                            toast.success('JD uploaded.')
                            if (jdInputRef.current) jdInputRef.current.value = ''
                          },
                          onError: (err) => toast.error(getErrorMessage(err)),
                        })
                      } else {
                        setPendingJdFile(file)
                      }
                    }}
                  />
                  {uploadJd.isPending && (
                    <span className="spinner-border spinner-border-sm text-primary" />
                  )}
                  {!isEditing && pendingJdFile && (
                    <span className="badge bg-success-transparent text-success fs-12 text-nowrap">
                      <i className="ti ti-check me-1" />{pendingJdFile.name}
                    </span>
                  )}
                </div>
                <div className="form-text">
                  {isEditing
                    ? 'Uploading a new file will replace the existing one.'
                    : 'Will be uploaded when the job is created.'}
                </div>
              </div>

              <Button type="submit" loading={mutation.isPending}>
                {isEditing ? 'Save Changes' : 'Create Job'}
              </Button>
            </form>
          </Card>
        </div>

        <div className="col-xl-4">
          <Card title="Screening Questions">
            {isEditing && job ? (
              <JobScreeningQuestionsSection jobId={job.id} questions={job.screeningQuestions} />
            ) : (
              <p className="text-muted fs-13">Save the job first to add screening questions.</p>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}
